import type { ReactElement } from 'react';
import { Area, AreaChart, Bar, BarChart, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import type { DashboardTrendDay } from '@/api/adminClient';
import { chartAxisTick, chartBarCursor, chartLineCursor, chartSeriesColor, chartTooltipProps } from '@/lib/chartTheme';
import { formatCount, formatUsd } from '@/lib/format';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-09-03" → "Sep 3". */
export function trendDayLabel(date: string): string {
  const [, month, day] = date.split('-').map(Number);
  return month && day ? `${MONTHS[month - 1]} ${day}` : date;
}

/** Change against the previous window: "+12%", "−40%", "New" when there was nothing before, or undefined when both are zero. */
export function trendDelta(current: number, previous: number): { label: string; direction: 'up' | 'down' | 'flat' } | undefined {
  if (current === 0 && previous === 0) return undefined;
  if (previous === 0) return { label: 'New', direction: 'up' };
  const change = (current - previous) / previous;
  const rounded = Math.round(change * 100);
  if (rounded === 0) return { label: '0%', direction: 'flat' };
  return { label: `${rounded > 0 ? '+' : '−'}${Math.abs(rounded)}%`, direction: rounded > 0 ? 'up' : 'down' };
}

const SERIES_LABELS: Record<string, string> = {
  llmCostUsd: 'LLM',
  integrationCostUsd: 'Integrations',
  runs: 'Builds',
  activeOrgs: 'Active organizations',
  newApps: 'New apps',
};

type ChartDay = DashboardTrendDay & { dateLabel: string };

function withLabels(days: DashboardTrendDay[]): ChartDay[] {
  return days.map((day) => ({ ...day, dateLabel: trendDayLabel(day.date) }));
}

/**
 * A small chart that sits under a summary number: no y-axis (the tooltip has
 * the values), only the first and last dates, so a row of three stays short.
 */
function Spark({ children }: { children: ReactElement }) {
  return (
    <div className="mt-3 h-24">
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}

function xAxis() {
  return <XAxis dataKey="dateLabel" tick={chartAxisTick} interval="preserveStartEnd" minTickGap={80} tickLine={false} axisLine={false} height={18} />;
}

function tooltip(format: (value: number) => string, cursor: typeof chartLineCursor | typeof chartBarCursor) {
  return (
    <Tooltip
      {...chartTooltipProps}
      cursor={cursor}
      formatter={(value, name) => [format(Number(value)), SERIES_LABELS[String(name)] ?? name]}
    />
  );
}

/** Usage per day, LLM and integrations stacked. */
export function UsageTrendChart({ days }: { days: DashboardTrendDay[] }) {
  return (
    <Spark>
      <AreaChart data={withLabels(days)} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        {xAxis()}
        {tooltip(formatUsd, chartLineCursor)}
        <Area type="monotone" dataKey="llmCostUsd" stackId="cost" stroke={chartSeriesColor(0)} fill={chartSeriesColor(0)} fillOpacity={0.18} strokeWidth={1.5} />
        <Area type="monotone" dataKey="integrationCostUsd" stackId="cost" stroke={chartSeriesColor(2)} fill={chartSeriesColor(2)} fillOpacity={0.16} strokeWidth={1.5} />
      </AreaChart>
    </Spark>
  );
}

/** Builds per day. */
export function BuildsTrendChart({ days }: { days: DashboardTrendDay[] }) {
  return (
    <Spark>
      <BarChart data={withLabels(days)} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        {xAxis()}
        {tooltip(formatCount, chartBarCursor)}
        <Bar dataKey="runs" fill={chartSeriesColor(1)} radius={[2, 2, 0, 0]} />
      </BarChart>
    </Spark>
  );
}

/** Active organizations per day (line) and new apps (bars). */
export function OrganizationsTrendChart({ days }: { days: DashboardTrendDay[] }) {
  return (
    <Spark>
      <ComposedChart data={withLabels(days)} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        {xAxis()}
        {tooltip(formatCount, chartBarCursor)}
        <Bar dataKey="newApps" fill={chartSeriesColor(3)} radius={[2, 2, 0, 0]} />
        <Line type="monotone" dataKey="activeOrgs" stroke={chartSeriesColor(0)} strokeWidth={1.5} dot={false} />
      </ComposedChart>
    </Spark>
  );
}

/** A coloured dot and name, for the few series a card shows. */
export function SeriesKey({ items }: { items: Array<{ label: string; colorIndex: number }> }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: chartSeriesColor(item.colorIndex) }} aria-hidden="true" />
          {item.label}
        </span>
      ))}
    </div>
  );
}
